import logo from '@assets/lilyLogo.png';
import { Menu } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

import WhatsAppButton from '../WhatsAppButton.jsx';
import { BRAND } from './headerModel';
import { useHeaderViewModel } from './useHeaderViewModel';

export const HeaderView = () => {
  const vm = useHeaderViewModel();

  // Estilos compartilhados para os itens de menu mobile.
  // Cores vêm da rampa rosa do v2 (design/tokens.css), não mais de
  // `wine` — que no index.css aponta para #f64a8a, um rosa que não é
  // o da marca e destoava das telas novas.
  const mobileItemClass =
    'flex items-center gap-3 w-full px-4 py-3 rounded-xl text-ink-soft hover:bg-h-rosa-wash hover:text-h-rosa-text transition-all duration-200 font-medium text-left';

  return (
    <header className="sticky top-0 z-50 w-full bg-paper/90 backdrop-blur-md border-b border-linen execution-context">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[4.5rem]">
          {/* ───── MOBILE ───── */}
          <div className="md:hidden flex items-center justify-between w-full relative h-full">
            {/* 1. MENU HAMBÚRGUER (Esquerda) */}
            <div className="flex items-center">
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label="Abrir menu de navegação"
                    className="p-1 -ml-1 text-h-rosa-text hover:text-h-rosa transition-colors rounded-lg focus-visible:ring-2 focus-visible:ring-h-rosa focus:outline-none"
                  >
                    <Menu className="h-8 w-8" />
                  </button>
                </SheetTrigger>

                <SheetContent
                  side="left"
                  className="w-[85vw] max-w-[340px] bg-paper border-r border-h-rosa-soft p-0 flex flex-col"
                >
                  <SheetHeader className="p-6 border-b border-h-rosa-soft text-left">
                    <SheetTitle className="text-ink-soft font-bold text-xl">
                      Menu
                    </SheetTitle>
                    <SheetDescription className="text-mauve text-sm">
                      Navegue pelo {BRAND.name}
                    </SheetDescription>
                  </SheetHeader>

                  <nav className="flex flex-col gap-1 px-2 py-4 flex-1 overflow-y-auto overflow-x-hidden">
                    {vm.navLinks?.map((link, index) => {
                      const isActive = vm.isActivePath(link.path);
                      return (
                        <SheetClose asChild key={link.path}>
                          <Link
                            to={link.path}
                            className={cn(
                              mobileItemClass,
                              isActive &&
                                'bg-h-rosa-wash text-h-rosa-text font-semibold',
                              'animate-in fade-in slide-in-from-left-8 duration-500 fill-mode-both',
                            )}
                            style={{ animationDelay: `${index * 75}ms` }}
                          >
                            {link.name}
                          </Link>
                        </SheetClose>
                      );
                    })}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>

            <Link
              to="/"
              className="absolute left-1/2 -translate-x-1/2 transition-opacity hover:opacity-90"
              aria-label="Ir para a página inicial"
            >
              <div className="w-[300px] h-[200px] flex items-center justify-center">
                <img
                  src={logo}
                  alt={`Logo ${BRAND.name}`}
                  className="w-full h-full object-contain"
                />
              </div>
            </Link>

            {/* Espaçador pra manter a logo centralizada */}
            <div className="w-8 h-8" aria-hidden="true" />
          </div>

          {/* ───── DESKTOP ───── */}
          <div className="hidden md:flex items-center justify-between w-full h-full">
            {/* LOGO DESKTOP (Com DIV de Limite Responsivo) */}
            <Link
              to="/"
              className="transition-opacity hover:opacity-90 shrink-0"
              aria-label="Ir para a página inicial"
            >
              <div className="w-[150px] lg:w-[180px] h-[200px] flex items-center justify-start">
                <img
                  src={logo}
                  alt={`Logo ${BRAND.name}`}
                  className="w-full h-full object-contain object-left"
                />
              </div>
            </Link>

            <div className="flex items-center gap-8">
              <nav className="flex items-center gap-6">
                {vm.navLinks?.map((link) => {
                  const isActive = vm.isActivePath(link.path);

                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={cn(
                        'text-sm font-medium relative py-2 transition-colors duration-200 focus-visible:text-h-rosa-text focus:outline-none',
                        isActive
                          ? 'text-h-rosa-text'
                          : 'text-ink-soft hover:text-h-rosa-text',
                      )}
                    >
                      {link.name}
                      {isActive && (
                        <span className="absolute bottom-0 left-0 w-full h-px bg-h-rosa" />
                      )}
                    </Link>
                  );
                })}
              </nav>

              {/* ACTIONS DESKTOP */}
              <WhatsAppButton
                text="Falar Conosco"
                className="bg-h-rosa-fill text-white hover:bg-h-rosa-fill/90 shadow-h-rosa/30"
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
